// @flow
import React, {useEffect, useState} from 'react';
import type {Node} from 'react';
import type {UIResourceAnswerType} from '@wellstone-solutions/common';
import type {UIResourceType} from '@wellstone-solutions/common/models/rest';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  IconNames,
  Stack,
} from '@wellstone-solutions/web';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  FLAG_NOTICE_DURATION_MS,
  FLAG_NOTICES,
  RESOURCE_MODAL_VIEWS,
} from '../../constants';
import {ResourceDetailView} from './ResourceDetailView';
import {ShareView} from './ShareView';
import {SaveResourceButton} from '../SaveResourceButton';
import {EditResourceButton} from './EditResourceButton';
import {FlagResourceButton} from './FlagResourceButton';
import {FlagResourceView} from '../FlagResourceView';
import type {FlagOutcome} from '../FlagResourceView';

type PropsType = {
  open: boolean,
  onClose: () => void,
  resource: null | UIResourceAnswerType | UIResourceType,
  defaultView?: string,
};

export const ResourceModal = ({
  open,
  onClose,
  resource,
  defaultView = RESOURCE_MODAL_VIEWS.detail,
}: PropsType): Node => {
  const [currentView, setCurrentView] = useState(defaultView);
  const [flagOutcome, setFlagOutcome] = useState<?FlagOutcome>(null);
  const [isNoticeVisible, setIsNoticeVisible] = useState(false);

  // Hide the notice after a few seconds; it clears once the collapse ends.
  useEffect(() => {
    if (!flagOutcome) return;
    setIsNoticeVisible(true);
    const timer = setTimeout(
      () => setIsNoticeVisible(false),
      FLAG_NOTICE_DURATION_MS,
    );
    return () => clearTimeout(timer);
  }, [flagOutcome]);
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Make sure the correct view is shown when the modal is opened
  useEffect(() => {
    if (open) {
      setCurrentView(defaultView);
      setFlagOutcome(null);
    }
  }, [open, defaultView]);

  // Determine modal type based on defaultView
  // These flags are used to determine which buttons to show in the header
  const isDetailModal = defaultView === RESOURCE_MODAL_VIEWS.detail;
  const isShareModal = defaultView === RESOURCE_MODAL_VIEWS.share;
  const isFlagView = currentView === RESOURCE_MODAL_VIEWS.flag;
  const showDetail = () => setCurrentView(RESOURCE_MODAL_VIEWS.detail);

  const handleFlagged = (outcome: FlagOutcome) => {
    setFlagOutcome(outcome);
    showDetail();
  };

  if (!resource) return null;

  return (
    <Dialog
      fullScreen={isFullScreen}
      fullWidth
      onClose={onClose}
      open={open}
      onTransitionExited={() => setCurrentView(defaultView)}
      PaperProps={{
        // The flag view sets the summary beside the form, so it needs room.
        sx: [
          styles.dialogPaper,
          isShareModal && {pt: 1},
          isFlagView && styles.flagPaper,
        ],
      }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={[styles.header, isShareModal && styles.noNav]}>
        {isDetailModal && (
          <Box>
            {currentView === RESOURCE_MODAL_VIEWS.detail && (
              <>
                <Button
                  onClick={() => setCurrentView(RESOURCE_MODAL_VIEWS.share)}
                  sx={styles.headerButton}
                  startIcon={
                    <Icon
                      name={IconNames.SendDiagonalSolid}
                      size={16}
                      color="primary"
                      style={styles.buttonIcon}
                    />
                  }>
                  Share
                </Button>

                <SaveResourceButton resourceId={resource.id} />

                <EditResourceButton
                  resourceId={resource.id}
                  onNavigate={onClose}
                />

                <FlagResourceButton
                  resource={resource}
                  onFlag={() => {
                    setFlagOutcome(null);
                    setCurrentView(RESOURCE_MODAL_VIEWS.flag);
                  }}
                />
              </>
            )}

            {isFlagView && (
              <Button
                onClick={showDetail}
                sx={styles.flagTitleButton}
                startIcon={<Icon name={IconNames.NavArrowLeft} size={20} />}>
                Flag this resource
              </Button>
            )}

            {currentView === RESOURCE_MODAL_VIEWS.share && (
              <Button
                onClick={() => setCurrentView(defaultView)}
                sx={styles.headerButton}
                startIcon={
                  <Icon
                    name={IconNames.ArrowLeft}
                    size={16}
                    color="primary"
                    style={styles.buttonIcon}
                  />
                }>
                Back
              </Button>
            )}
          </Box>
        )}

        <IconButton
          onClick={onClose}
          icon={IconNames.Xmark}
          iconProps={{
            name: IconNames.Xmark,
            style: styles.buttonIcon,
          }}
          data-testid="close-dialog"
        />
      </Stack>

      {currentView === RESOURCE_MODAL_VIEWS.detail && flagOutcome && (
        <Collapse
          in={isNoticeVisible}
          appear
          onExited={() => setFlagOutcome(null)}>
          <DialogContent sx={styles.flagNotice}>
            <Alert
              severity={FLAG_NOTICES[flagOutcome].severity}
              title={FLAG_NOTICES[flagOutcome].title}>
              {FLAG_NOTICES[flagOutcome].message}
            </Alert>
          </DialogContent>
        </Collapse>
      )}

      {currentView === RESOURCE_MODAL_VIEWS.detail && (
        <ResourceDetailView resource={resource} onClose={onClose} />
      )}

      {isFlagView && (
        <FlagResourceView
          resource={resource}
          onCancel={showDetail}
          onFlagged={handleFlagged}
        />
      )}

      {currentView === RESOURCE_MODAL_VIEWS.share && (
        <ShareView resource={resource} onClose={onClose} />
      )}
    </Dialog>
  );
};

const styles = {
  dialogPaper: {
    borderRadius: {
      xs: 0,
      sm: 3,
    },
    maxWidth: '750px',
  },
  flagPaper: {
    maxWidth: '900px',
  },
  flagTitleButton: {
    fontSize: 20,
    fontWeight: 700,
    textTransform: 'none',
    color: 'primary.main',
    '& .MuiButton-startIcon': {mr: 0.5, ml: 0},
  },
  flagNotice: {
    pt: 2,
    pb: 0,
  },
  header: {
    padding: 2,
    paddingBottom: 0,
  },
  noNav: {
    position: 'absolute',
    right: 8,
    top: 16,
    padding: 0,
  },
  headerButton: {
    textTransform: 'none',
    '& .MuiButton-startIcon': {
      marginRight: 0.75,
      marginLeft: 0,
    },
  },
  buttonIcon: {
    strokeWidth: 2,
  },
};
